import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {skip} from 'rxjs';
import {Router, RouterLink, RouterLinkActive, RouterOutlet} from "@angular/router";
import {CookieHelper} from "../../helpers/CookieHelper";
import {AuthenticationService} from "../../_auth/authentication.service";
import {UserJwt} from "../../models/admin_owner/user/user-jwt";
import {EmployeePanelRoutes} from "./employee-panel-routes";
import {Constants} from "../../constants";
import {DatePipe, NgClass} from "@angular/common";
import {SidebarComponent} from "./sidebar/sidebar.component";
import {ContextSwitcherComponent} from "../../_auth/context/context-switcher.component";
import {FormsGateHttpService} from "../forms/data-access/forms-gate-http.service";
import {AppRoutes} from "../../enums/routes";

/**
 * Height of the employee panel's own top bar, in pixels — the `h-14` on the bar in
 * `employee-panel.component.html`, and the `top-14` every element below it is offset by.
 *
 * It is **56 px at every breakpoint**: the bar has no responsive height. Exported so anything positioned
 * against this shell (notably the fixed `AlertBannerComponent`, which is placed by its own `top-*` and never
 * by this shell's padding) can be pinned to the real number instead of re-deriving it — #223 was exactly that
 * mistake, an offset of 40/80 px against a 56 px bar.
 */
export const EMPLOYEE_PANEL_TOP_BAR_HEIGHT_PX = 56;

/**
 * Height of the review-due strip the panel shows under its top bar while a submission is flagged for review —
 * the `h-10` on that banner in `employee-panel.component.html`.
 *
 * While it is up the shell's chrome is {@link EMPLOYEE_PANEL_TOP_BAR_HEIGHT_PX} + this = 96 px, which is why
 * the panel's own content switches to `top-24` in that state.
 *
 * **There is deliberately only ever ONE strip**, whatever the gate is warning about (see {@link GateBannerKind}).
 * `AlertBannerComponent.TopOffsetClass` restates this chrome height as a single `top-24`, so stacking a second
 * strip would silently push the global alert banner under it.
 */
export const EMPLOYEE_PANEL_REVIEW_BANNER_HEIGHT_PX = 40;

/**
 * What the panel's warning strip is about.
 *
 * - `pending` — a form was newly assigned with the **Grace** backfill policy and is still inside its 14-day
 *   Frist: nothing is wrong yet, the employee simply has to fill it in before the deadline ("Formular ausstehend").
 * - `review` — a completed submission is flagged, either by a reviewer or because the form's Gültigkeitsdauer
 *   elapsed, and must be handled before the deadline ("Prüfung fällig").
 * - `contact` — a required contact channel is not linked yet, or a link is due to be re-confirmed, and the
 *   Frist has not run out ("Verknüpfung ausstehend", #558). A SECOND gate, sharing the one strip: see
 *   {@link EMPLOYEE_PANEL_REVIEW_BANNER_HEIGHT_PX} for why there can only ever be one.
 */
export type GateBannerKind = 'pending' | 'review' | 'contact';

/** The strip's content: which kind of warning, and the deadline it is counting down to. */
export interface GateBanner {
  kind: GateBannerKind;
  deadline: Date | null;
}

@Component({
  selector: 'app-employee-panel',
  templateUrl: './employee-panel.component.html',
  imports: [
    RouterLinkActive,
    RouterLink,
    NgClass,
    DatePipe,
    SidebarComponent,
    RouterOutlet,
    ContextSwitcherComponent
  ],
  styleUrls: ['./employee-panel.component.scss']
})
export class EmployeePanelComponent implements OnInit {
  user: UserJwt | null;
  sidebar: boolean = true;
  protected readonly EmployeePanelRoutes = EmployeePanelRoutes;
  protected readonly AppRoutes = AppRoutes;
  protected readonly Router = Router;

  private readonly gate = inject(FormsGateHttpService);

  /**
   * What the strip says. Seeded from the session's own claim — which carries the deadline but *not* which of the
   * two situations produced it — and refined from `/auth/forms-gate/status` as soon as that answers. Seeding
   * rather than starting empty keeps the chrome height stable: the strip is shown for the whole time the claim
   * says `review`, so the content region never re-offsets mid-render.
   */
  protected readonly banner = signal<GateBanner>({kind: 'review', deadline: null});

  constructor(private router: Router,
              private authenticationService: AuthenticationService) {
    this.user = this.authenticationService.userValue
  }

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.computeBanner();

    // Re-run on every session swap. The live gate refresh (#558) re-mints the token UNDER a running panel:
    // a session that was clean at init can start warning minutes later, and the strip then appears (the
    // template reads `showsBanner` live) — but it would show whatever this signal held, i.e. the seeded forms
    // default with no deadline. That is exactly what happened on the first manual smoke: a due contact
    // re-verification rendered as „Prüfung fällig" and linked to the forms. `skip(1)` drops the replayed
    // current session, which `computeBanner()` above has already consumed.
    this.authenticationService.user?.pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.computeBanner());
  }

  /** Decides what the ONE strip says for the session as it is right now. Idempotent; see {@link ngOnInit}. */
  private computeBanner(): void {
    if (!this.showsBanner) return;

    // No forms business ⇒ the contact gate is the only thing warning, and its claim carries everything the
    // strip needs. Nothing to ask anybody.
    if (!this.isReviewDue) {
      this.banner.set({kind: 'contact', deadline: this.contactReviewDue});
      return;
    }

    this.banner.set({kind: 'review', deadline: this.reviewDue});
    // The claim cannot tell a pending backfill from a review flag, so ask the gate which it is. On failure the
    // seeded review wording stands — the same warning the panel showed before pending forms existed.
    this.gate.status().subscribe({
      next: status => {
        const pending = earliest((status.pending ?? []).map(p => p.deadline));
        const flagged = earliest((status.flagged ?? []).map(f => f.deadline));
        if (pending || flagged) {
          // Both can be live at once; the nearer deadline is the one the employee has to act on first.
          const showPending = !!pending && (!flagged || pending.getTime() <= flagged.getTime());
          this.banner.set(showPending
            ? {kind: 'pending', deadline: pending}
            : {kind: 'review', deadline: flagged});
        }
        this.preferContactIfSooner();
      },
      error: () => {
        // Keep the seeded fallback; a warning with the right deadline beats no warning at all. The contact
        // comparison still runs: its deadline comes off the session claim, which a failed forms call cannot
        // affect, and skipping it here would hide the sooner of the two warnings whenever Forms is degraded.
        this.preferContactIfSooner();
      }
    });
  }

  /**
   * With BOTH gates warning, the strip shows whichever deadline comes first.
   *
   * There is only one strip (see {@link EMPLOYEE_PANEL_REVIEW_BANNER_HEIGHT_PX}), so this is a genuine choice
   * rather than a layout accident, and "soonest first" is the only ordering that helps the reader: a contact
   * re-confirmation due tomorrow matters more than a form due in a month, whatever the gates' priority order
   * says about which SCREEN a restricted session is pinned to. The tie goes to forms, matching that order.
   *
   * Run after the forms refinement rather than before it, because the forms deadline this compares against is
   * the refined one — the claim's own deadline cannot tell a pending backfill from a review flag.
   */
  private preferContactIfSooner(): void {
    const contact = this.contactReviewDue;
    if (!this.isContactReviewDue || !contact) return;

    const current = this.banner().deadline;
    if (current && current.getTime() <= contact.getTime()) return;
    this.banner.set({kind: 'contact', deadline: contact});
  }

  /** True when ANY gate wants the strip up — the one question the shell's chrome height depends on. */
  get showsBanner(): boolean {
    return this.authenticationService.isGateReviewDue;
  }

  /** True when the session is warned + time-boxed by the required-forms gate. */
  get isReviewDue(): boolean {
    return this.authenticationService.isFormsReviewDue;
  }

  /** The forms deadline carried on the session token (null when not in the warned state). */
  get reviewDue(): Date | null {
    return this.authenticationService.formsReviewDue;
  }

  /** True when the session is warned + time-boxed by the required-contact gate (#558). */
  get isContactReviewDue(): boolean {
    return this.authenticationService.isContactReviewDue;
  }

  /** The contact deadline carried on the session token (null when not in that state). */
  get contactReviewDue(): Date | null {
    return this.authenticationService.contactReviewDue;
  }

  logout() {
    //CookieHelper.deleteCookie("JWT");
    CookieHelper.deleteCookie(Constants.Cookies.Hangfire);
    this.authenticationService.logout();
  }
}

/** The earliest of a set of ISO deadlines, or null when there are none (invalid dates are ignored). */
function earliest(deadlines: (string | Date)[]): Date | null {
  const times = deadlines.map(d => new Date(d).getTime()).filter(t => Number.isFinite(t));
  return times.length ? new Date(Math.min(...times)) : null;
}
